// SPDX-License-Identifier: GPL-3.0-or-later
// The sound, effects, music, vibration and background rows of the Settings dialog.
//
// The rows show the store. A change of a row goes to the store. A value that the store refuses is
// shown again as the store has it. A store change (for example a reset) updates every row. The
// dialog root comes in as an argument, so the binder runs in a test with no window. A control that
// is missing from the markup is skipped, not an error.

import type { FeedbackSettings, SettingsStore } from "../src/shared/feedback/settings.ts";

/** The part of the dialog that finds the controls. `Element.querySelector` fits it. */
export interface SettingsRoot {
  querySelector(selector: string): unknown;
}

/** What the binder takes from the device. Every field is optional. */
export interface FeedbackSettingsEnv {
  /** True when the device can vibrate. The default reads `navigator.vibrate`. */
  readonly canVibrate?: boolean;
}

/** The part of a form control that the binder uses. */
interface Control {
  checked?: boolean;
  value?: string;
  hidden?: boolean;
  addEventListener(type: string, listener: () => void): void;
}

type SettingKey = keyof FeedbackSettings;

/** How one control shows and reads one setting. */
interface FieldSpec {
  readonly slot: string;
  readonly key: SettingKey;
  /** The value that the control holds now. The store checks it. */
  read(control: Control): unknown;
  /** Show a value on the control. */
  write(control: Control, value: unknown): void;
}

/** A check box that holds a yes or no setting. */
function toggle(slot: string, key: SettingKey): FieldSpec {
  return {
    slot,
    key,
    read: (c) => c.checked === true,
    write: (c, v) => {
      c.checked = v === true;
    },
  };
}

/** A range that holds a volume. The text of the range is a number. */
function amount(slot: string, key: SettingKey): FieldSpec {
  return {
    slot,
    key,
    read: (c) => Number(c.value),
    write: (c, v) => {
      c.value = String(v);
    },
  };
}

/** A select that holds one of a list of names. The store checks the name. */
function choice(slot: string, key: SettingKey): FieldSpec {
  return {
    slot,
    key,
    read: (c) => c.value ?? "",
    write: (c, v) => {
      c.value = String(v);
    },
  };
}

/** The rows of the dialog. The slot names are the `data-slot` values in public/index.html. */
const FIELDS: readonly FieldSpec[] = [
  toggle("fx-sound", "soundEnabled"),
  amount("fx-volume", "volume"),
  toggle("fx-music", "musicEnabled"),
  amount("fx-music-volume", "musicVolume"),
  choice("fx-level", "vfxLevel"),
  toggle("fx-reduce-flashing", "reduceFlashing"),
  toggle("fx-haptics", "haptics"),
  choice("fx-stream", "streamBackground"),
];

/** The slot of the row that holds the vibration check box. It is hidden with no vibration. */
const HAPTICS_ROW_SLOT = "fx-haptics-row";

interface Bound {
  readonly spec: FieldSpec;
  readonly control: Control;
}

/** Find an element of the dialog by its slot. A missing element gives `null`. */
function findElement(root: SettingsRoot, slot: string): Control | null {
  return (root.querySelector(`[data-slot="${slot}"]`) as Control | null) ?? null;
}

/** True when `navigator.vibrate` exists. */
function defaultCanVibrate(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.vibrate === "function";
}

/** Show or hide the vibration row. */
function showVibrationRow(root: SettingsRoot, canVibrate: boolean): void {
  const row = findElement(root, HAPTICS_ROW_SLOT);
  if (row !== null) row.hidden = !canVibrate;
}

/** Find the controls that the markup has. */
function collectBound(root: SettingsRoot): Bound[] {
  const bound: Bound[] = [];
  for (const spec of FIELDS) {
    const control = findElement(root, spec.slot);
    if (control !== null) bound.push({ spec, control });
  }
  return bound;
}

/**
 * Bind the settings rows of the dialog to the store. Return a function that unbinds them: after it
 * runs, a change of a row does not reach the store, and a store change does not reach a row.
 */
export function bindFeedbackSettings(
  root: SettingsRoot,
  store: SettingsStore,
  env: FeedbackSettingsEnv = {},
): () => void {
  const bound = collectBound(root);
  let alive = true;

  const show = (settings: FeedbackSettings): void => {
    for (const { spec, control } of bound) spec.write(control, settings[spec.key]);
  };

  const onInput = (item: Bound): void => {
    if (!alive) return;
    const patch = { [item.spec.key]: item.spec.read(item.control) } as Partial<FeedbackSettings>;
    store.set(patch);
    show(store.get());
  };

  for (const item of bound) {
    item.control.addEventListener("input", () => onInput(item));
    item.control.addEventListener("change", () => onInput(item));
  }

  const unsubscribe = store.subscribe((settings) => {
    if (alive) show(settings);
  });
  showVibrationRow(root, env.canVibrate ?? defaultCanVibrate());
  show(store.get());

  return () => {
    alive = false;
    unsubscribe();
  };
}
